import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { Input, Popover, Spin, Carousel } from 'antd';
import { inject, observer } from 'mobx-react';
import Button from '../../common/Button/Button';
import { text2Medium, text3 } from '../../../constants/classes';
import { colors } from '../../../constants';
import IconSvg from '../../common/IconSvg';
import { InfoText } from '../../common/styled';
import IconPng from '../../common/IconPng';

const DataRow = styled.div`
  width: 60%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  height: 50px;
  position: relative;
  ${text2Medium}
`;

const BtnWrapper = styled.div`
  width: 60%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

function CSD({ azdConnection, closeForm, spinning, setSpinner }) {
  const {
    setAccountName,
    setSellerId,
    setMwsAuthToken,
    connectSeller,
    mwsAuthToken,
    accountName,
    sellerId,
    accountValid,
    inputsValid,
    reinit,
  } = azdConnection;
  return (
    <>
      <InfoText>
        <FormattedMessage id="API.azDestStep2Info" />
      </InfoText>
      <DataRow>
        Account Name
        <span
          css={css`
            ${text3}
          `}
        >
          <Input
            value={accountName}
            onChange={({ target: { value } }) => {
              setAccountName(value);
            }}
          />
        </span>
        <h4
          css={css`
            position: absolute;
            bottom: 0;
            color: ${colors.pinkDark};
            font-family: Heebo-Thin;
          `}
        >
          {accountName && !accountValid && '* Please input a valid email'}
        </h4>
      </DataRow>
      <DataRow>
        Seller Id
        <span
          css={css`
            ${text3}
          `}
        >
          <Input
            value={sellerId}
            onChange={({ target: { value } }) => {
              setSellerId(value);
            }}
          />
        </span>
      </DataRow>
      <DataRow>
        MWS Token
        <span
          css={css`
            ${text3}
          `}
        >
          <Input
            value={mwsAuthToken}
            onChange={({ target: { value } }) => {
              setMwsAuthToken(value);
            }}
          />
        </span>
      </DataRow>
      <BtnWrapper>
        <Button
          clickHandler={() => {
            setSpinner(true);
            connectSeller().then(() => {
              reinit();
              setSpinner(false);
              closeForm();
            });
          }}
          type="normal"
          disabled={!inputsValid}
          loading={spinning}
        >
          <FormattedMessage id="API.finish" />
        </Button>
      </BtnWrapper>
    </>
  );
}

const ConnectSellerData = inject('azdConnection')(observer(CSD));

const PopOverInfo = ({ carouselRef }) => {
  return (
    <div
      css={css`
        width: 600px;
        padding: 0 25px;
      `}
    >
      <Carousel
        ref={carouselRef}
        infinite={false}
        dots={false}
        arrows
        slidesToShow={1}
        slidesToScroll={1}
        nextArrow={<Arrow type="carousel-arrow-r" />}
        prevArrow={<Arrow flip type="carousel-arrow-r" />}
      >
        <InfoText
          key={1}
          css={css`
            display: flex !important;
            flex-direction: column;
            align-items: center;
          `}
        >
          <div
            css={css`
              align-self: flex-start;
              margin-bottom: 20px;
            `}
          >
            <FormattedMessage id="API.azDestStep1InfoDetails1" />
          </div>
          <IconPng type="amazon/destination/step1-az-dest" size={400} />
        </InfoText>
        <InfoText
          key={2}
          css={css`
            display: flex !important;
            flex-direction: column;
            align-items: center;
          `}
        >
          <div
            css={css`
              align-self: flex-start;
              margin-bottom: 20px;
            `}
          >
            <FormattedMessage id="API.azDestStep1InfoDetails2" />
          </div>
          <IconPng type="amazon/destination/step2-az-dest" size={400} />
        </InfoText>
        <InfoText
          key={3}
          css={css`
            display: flex !important;
            flex-direction: column;
            align-items: center;
          `}
        >
          <div
            css={css`
              align-self: flex-start;
              margin-bottom: 20px;
            `}
          >
            <FormattedMessage id="API.azDestStep1InfoDetails3" />
          </div>
          <IconPng type="amazon/destination/step3-az-dest" size={400} />
        </InfoText>
      </Carousel>
    </div>
  );
};

const Arrow = styled(IconSvg)`
  &:before {
    content: none !important;
  }
  transform: ${props => (props.flip ? 'rotate(180deg)' : '')};
`;

function AmazonConnect({ handleConnect, connectButtonPressed, developerData, closeForm }) {
  const [step, setStep] = useState(1);
  // const [helpVisible, setHelpVisible] = useState(false);
  const [spinning, setSpinner] = useState(false);
  const refContainer = useRef(React.createRef());
  return (
    <>
      {connectButtonPressed && step === 1 && (
        <>
          <InfoText>
            <FormattedMessage id="API.azDestStep1Info1" />
            <Popover
              title={null}
              placement="left"
              onVisibleChange={vis => {
                if (vis === false) {
                  refContainer.current.goTo(0);
                }
              }}
              content={<>{<PopOverInfo carouselRef={refContainer} />}</>}
            >
              <span
                css={css`
                  cursor: pointer;
                `}
              >
                <u>
                  <FormattedMessage id="API.azDestStep1Info2" />
                </u>
              </span>
            </Popover>
          </InfoText>
          <DataRow>
            <FormattedMessage id="API.developerName" />
            <span
              css={css`
                ${text3}
              `}
            >
              {spinning ? <Spin /> : developerData?.developerName}
            </span>
          </DataRow>
          <DataRow>
            <FormattedMessage id="API.developerId" />
            <span
              css={css`
                ${text3}
              `}
            >
              {spinning ? <Spin /> : developerData?.developerId}
            </span>
          </DataRow>
          <BtnWrapper>
            <Button clickHandler={() => setStep(2)} type="normal">
              <FormattedMessage id="API.next" />
            </Button>
          </BtnWrapper>
        </>
      )}
      {connectButtonPressed && step === 2 && (
        <ConnectSellerData closeForm={closeForm} spinning={spinning} setSpinner={setSpinner} />
      )}
      {!connectButtonPressed && handleConnect && (
        <BtnWrapper
          css={css`
            margin-top: 10px;
          `}
        >
          <Button
            clickHandler={() => {
              setSpinner(true);
              handleConnect().then(() => {
                setSpinner(false);
              });
            }}
            type="normal"
          >
            <FormattedMessage id="API.connect" defaultMessage="Connect" />
          </Button>
        </BtnWrapper>
      )}
    </>
  );
}

export default AmazonConnect;
